import { useEffect, useState } from "react";
import {
  ArrowLeft,
  BellRing,
  ExternalLink,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/Urls";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const NoticeDetails = () => {
  const { noticeId } = useParams();
  const navigate = useNavigate();

  const [notice, setNotice] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await api.get(
          `/api/notices/${encodeURIComponent(noticeId)}`
        );

        if (!cancelled) {
          setNotice(res.data);
          setState("ready");
        }
      } catch (error) {
        if (cancelled) return;

        if (error.response?.status === 401) {
          setState("auth");
        } else if (error.response?.status === 404) {
          setState("missing");
        } else {
          setState("error");
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [noticeId]);

  const publishedAt = notice?.createdAt
    ? new Date(notice.createdAt)
    : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="container mx-auto flex-1 px-4 py-8">
        <div className="mx-auto max-w-3xl">
          <button
            type="button"
            onClick={() =>
              window.history.length > 1
                ? navigate(-1)
                : navigate("/")
            }
            className="mb-5 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          {state === "loading" && (
            <div className="rounded-2xl border border-border bg-card p-10">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="h-9 w-9 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
                <p className="text-sm font-medium">
                  Loading notice...
                </p>
              </div>
            </div>
          )}

          {state === "auth" && (
            <div className="rounded-2xl border border-border bg-card p-8 text-center">
              <BellRing className="mx-auto mb-3 h-8 w-8 text-primary" />
              <h1 className="font-heading text-xl font-bold">
                Sign in to view this notice
              </h1>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Sign in to your DealSwapify account to view
                this notice.
              </p>
              <Link
                to="/signin"
                className="mt-5 inline-flex rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                Sign in
              </Link>
            </div>
          )}

          {state === "missing" && (
            <div className="rounded-2xl border border-border bg-card p-8 text-center">
              <BellRing className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <h1 className="font-heading text-xl font-bold">
                Notice unavailable
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This notice may have been deleted or is no
                longer available.
              </p>
            </div>
          )}

          {state === "error" && (
            <div className="rounded-2xl border border-destructive/30 bg-card p-8 text-center">
              <h1 className="font-heading text-xl font-bold">
                Unable to load notice
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Please try again later.
              </p>
            </div>
          )}

          {state === "ready" && notice && (
            <article className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              {notice.imageUrl && (
                <img
                  src={notice.imageUrl}
                  alt=""
                  className="max-h-[520px] w-full object-cover"
                />
              )}

              <div className="p-5 sm:p-7">
                <div className="flex items-center gap-2 text-primary">
                  <BellRing className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    DealSwapify Notice
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-2xl font-bold sm:text-3xl">
                  {notice.title}
                </h1>

                {publishedAt &&
                  !Number.isNaN(publishedAt.getTime()) && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Published{" "}
                      {publishedAt.toLocaleString()}
                    </p>
                  )}

                <div className="mt-6 whitespace-pre-wrap text-sm leading-7 text-foreground sm:text-base">
                  {notice.message}
                </div>

                {notice.actionUrl && (
                  <Link
                    to={notice.actionUrl}
                    className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
                  >
                    Continue
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </article>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NoticeDetails;
